import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { projectIdState } from "atoms/atoms-admin";
import { useRecoilValue } from "recoil";
import ButtonCell from "components/EditComponents/ButtonCell";
import { ElementFullDetails, SystemApi } from "features/brm";
import { TYPE } from "constants/brm";
import * as ReactQuery from "@tanstack/react-query";
import { useRoles } from "features/brm/hooks/useRoles";

import {
  createColumnBooleanMapped,
  createColumnActions,
  createColumnCategoryMapped,
} from "brm/tables/services/column/columnFactory";
import { LoadingSpinner as Loading } from "components/elements";
import { RoutePath } from "routes/route-paths";
import ResourceDetails from "../details/ResourceDetails";

function ResourceFullDetails() {
  const { isSystemEngineer, isRiskAnalyst } = useRoles();

  const { id } = useParams();
  const navigate = useNavigate();
  const projectId = useRecoilValue<any>(projectIdState);
  const localProjectId = useRef(projectId);

  const queryClient = ReactQuery.useQueryClient();
  const { data: resourceData } = SystemApi.useResource({
    resourceId: id,
    options: { enabled: isRiskAnalyst || isSystemEngineer },
  });
  const { data: elementCats } = SystemApi.useElementCategories({
    projectId,
    options: { enabled: isRiskAnalyst || isSystemEngineer },
  });

  const [selectedElement, setSelectedElement] = useState({});
  const [editMode, setEditMode] = useState(false);
  const [selectedRowId, setSelectedRowId] = useState("");
  const updatedCategory = useRef("");
  const updatedIsInternal = useRef("");

  const { mutate: setResourceCategory } = SystemApi.useSetResourceCategory({
    options: {
      onSettled: () => {
        updatedCategory.current = "";
      },
    },
  });
  const { mutate: setResourceIsInternal } = SystemApi.useSetResourceIsInternal({
    options: {
      onSettled: () => {
        updatedIsInternal.current = "";
      },
    },
  });

  const createButton = useCallback(
    (cellProps) => {
      return (
        <ButtonCell
          selectedRowId={selectedRowId}
          elementId={cellProps.cell.row.original.id}
          handleConfirmEditClick={async () => {
            setEditMode(false);

            if (updatedIsInternal.current !== "") {
              setResourceIsInternal({ resourceId: selectedRowId, isInternal: updatedIsInternal.current });
            }

            if (updatedCategory.current !== "") {
              setResourceCategory({ resourceId: selectedRowId, category: updatedCategory.current });
            }
            queryClient.invalidateQueries(SystemApi.resourceKeys.all);
          }}
          setEditMode={setEditMode}
          editMode={editMode}
        />
      );
    },

    [selectedRowId, editMode, queryClient, setResourceIsInternal, setResourceCategory]
  );

  const columns = useMemo(() => {
    if (isSystemEngineer) {
      return [
        createColumnBooleanMapped("isInternal", selectedRowId, editMode, updatedIsInternal),
        createColumnCategoryMapped("category", selectedRowId, elementCats, editMode, updatedCategory),
        createColumnActions(createButton, { disableFilters: true }),
      ];
    }

    return [
      createColumnBooleanMapped("isInternal", selectedRowId, false, null),
      createColumnCategoryMapped("category", selectedRowId, elementCats, false, null),
    ];
  }, [isSystemEngineer, selectedRowId, elementCats, editMode, createButton]);

  useEffect(() => {
    if (projectId !== localProjectId.current) {
      navigate(RoutePath.Resources);
    }
  }, [projectId, navigate]);

  useEffect(() => {
    if (resourceData) {
      setSelectedElement(resourceData);
    }
  }, [resourceData]);

  if (resourceData && selectedElement) {
    const detailsComp = <ResourceDetails selectedElement={resourceData} />;

    return (
      <ElementFullDetails
        showBackLink
        data={[resourceData]}
        columns={columns}
        selectedElement={resourceData}
        setSelectedElement={setSelectedElement}
        setSelectedRowId={setSelectedRowId}
        detailsComp={detailsComp}
        compName={TYPE.resource}
      />
    );
  }
  return <Loading />;
}

export default ResourceFullDetails;
